import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Admin one-shot backlog clearance.
 *
 * Under-staffed dealership occasionally needs to zero out months of
 * pending RTO / post-sale / approval work. Each section performs a
 * DRY-RUN first (safe — returns counts only). Only after the counts
 * look right does the operator hit Execute, which writes the change
 * with a full audit trail (`bulk_closed_*` fields on each row).
 */
const SECTIONS = [
  {
    key: "force-close-all",
    title: "☢ Nuclear one-shot: FORCE-CLOSE every in-progress registration",
    description:
      "Single Mongo update_many — closes ALL registrations currently `in_progress` in ONE round-trip (immune to memory ceiling / Cloudflare timeout that was killing the streaming bulk-close on the 4 000+ migrated-registration backlog). Marks every remaining pending stage as skipped, flips overall_status to completed, and promotes linked vehicles from reserved → registered. Requires live-run confirmation token 'CLOSE ALL' as a safety fence.",
    endpoint: "/registrations/admin/force-close-all-in-progress",
    payload: (reason, dry_run) => ({
      reason,
      dry_run,
      ...(dry_run ? {} : { confirm: "CLOSE ALL" }),
    }),
    countKey: "registrations_closed",
    countLabel: "Registrations closed",
    rowKey: "registrations_scanned",
    rowNoun: "registrations",
  },
  {
    key: "repair-migrated",
    title: "☢ Nuclear one-shot: REPAIR migrated registrations (vehicle already registered)",
    description:
      "For every registration whose linked VEHICLE is already `status=registered` but which still has pending / in-progress stages, mark those stages as skipped and flip overall_status to completed. Handles the migrated-sales case where the deal & RTO are done externally but the pipeline row is stuck with 5 pending KYC/Vahan/RTO-payment stages (the standard Nuclear force-close skips these because overall_status is already 'completed'). Requires live-run confirmation token 'REPAIR MIGRATED' as a safety fence.",
    endpoint: "/registrations/admin/repair-registered-vehicle-registrations",
    payload: (reason, dry_run) => ({
      reason,
      dry_run,
      ...(dry_run ? {} : { confirm: "REPAIR MIGRATED" }),
    }),
    countKey: "registrations_repaired",
    countLabel: "Registrations repaired",
    rowKey: "registrations_scanned",
    rowNoun: "registrations",
  },
  {
    key: "both",
    title: "★ Close ALL pending stages (RTO + Post-Sale in one shot)",
    description:
      "Recommended — closes every pending stage on every registration in one pass and moves the row to completed. Use this instead of running RTO + Post-Sale separately.",
    endpoint: "/registrations/admin/bulk-close-pending",
    payload: (reason, dry_run) => ({ phase: "both", reason, dry_run, include_today: true }),
    countKey: "stages_marked_skipped",
    rowKey: "registrations_touched",
    rowNoun: "registrations",
  },
  {
    key: "rto",
    title: "Close pending Registration (RTO) stages only",
    description:
      "Marks every RTO-phase stage still pending as SKIPPED on registrations created before today. Post-sale stages are left alone — the reg will remain in_progress until post-sale is also cleared.",
    endpoint: "/registrations/admin/bulk-close-pending",
    payload: (reason, dry_run) => ({ phase: "rto", reason, dry_run }),
    countKey: "stages_marked_skipped",
    rowKey: "registrations_touched",
    rowNoun: "registrations",
  },
  {
    key: "post_sale",
    title: "Close pending Post-Sale tasks only",
    description:
      "Marks every post-sale stage still pending (DMS, PDI, feedback, service reminders, renewal) as SKIPPED. RTO stages are left alone.",
    endpoint: "/registrations/admin/bulk-close-pending",
    payload: (reason, dry_run) => ({ phase: "post_sale", reason, dry_run }),
    countKey: "stages_marked_skipped",
    rowKey: "registrations_touched",
    rowNoun: "registrations",
  },
  {
    key: "approvals",
    title: "Approve pending Sale verifications",
    description:
      "Approves every sale with approval_status = pending created before today. Financial risk — verify counts before executing. Stamps bulk_approved_by / bulk_approve_reason for audit.",
    endpoint: "/sales/admin/bulk-approve-pending",
    payload: (reason, dry_run) => ({ reason, dry_run, include_today: true }),
    countKey: "sales_to_approve",
    rowKey: "sales_to_approve",
    rowNoun: "sales",
  },
  {
    key: "margin-leak",
    title: "One-shot: clean leaked Margin items from Packages",
    description:
      "Strips dealer_margin / mandatory_margin / helmet_margin / add_acc_margin rows that were mistakenly imported as MAND items by the Feb-2026 CSV bug. Recomputes package.price, promotes values to top-level margin fields, and scrubs matching entries from every sale/booking snapshot. Run this AFTER the code fix is redeployed.",
    endpoint: "/packages/admin/clean-margin-item-leak",
    payload: (reason, dry_run) => ({ reason, dry_run }),
    countKey: "item_rows_removed",
    rowKey: "packages_affected",
    rowNoun: "packages",
  },
  {
    key: "promote-vehicles",
    title: "One-shot: promote Reserved vehicles → Registered (for closed regs)",
    description:
      "Scans every registration with overall_status = completed and promotes its linked vehicle from `reserved` to `registered`. Fixes vehicles stranded in the Reserved bucket after the earlier bulk-close (the reg was closed but the vehicle status transition wasn't wired in).",
    endpoint: "/registrations/admin/promote-completed-vehicles",
    payload: (reason, dry_run) => ({ reason, dry_run }),
    countKey: "vehicles_promoted",
    rowKey: "completed_registrations_scanned",
    rowNoun: "registrations",
  },
  {
    key: "flush-in-flight",
    title: "☢ Nuclear one-shot: FORCE every Reserved + Sold vehicle → Registered",
    description:
      "Flushes the Inventory Stockpile to a clean-slate state — every vehicle currently `reserved` or `sold` is force-promoted to `registered`, regardless of whether a registration record exists or is complete. Available stock is NEVER touched. Use this ONCE to zero out historical drift (approved sales without registrations, stuck reserved rows, orphaned bookings). Requires live-run confirmation token 'PROMOTE ALL' as a safety fence — not reversible via UI.",
    endpoint: "/registrations/admin/promote-in-flight-vehicles",
    payload: (reason, dry_run) => ({
      reason,
      dry_run,
      ...(dry_run ? {} : { confirm: "PROMOTE ALL" }),
    }),
    countKey: "vehicles_promoted",
    rowKey: "total_scanned",
    rowNoun: "vehicles (reserved + sold)",
  },
];

function SectionCard({ section }) {
  const [reason, setReason] = useState("Backlog clearance — under-staffed pending cleanup");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const run = async (dry_run) => {
    setLoading(true);
    setConfirming(false);
    try {
      const { data } = await api.post(
        section.endpoint,
        section.payload(reason, dry_run),
      );
      setResult({ ...data, at: new Date().toLocaleString() });
      if (!dry_run) toast.success(`${section.title} — executed. See breakdown below.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-canvas-line p-6 space-y-4" data-testid={`bulk-section-${section.key}`}>
      <div>
        <div className="text-sm font-semibold">{section.title}</div>
        <div className="text-xs text-muted-foreground mt-1">{section.description}</div>
      </div>

      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Audit reason</span>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
          data-testid={`bulk-reason-${section.key}`}
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => run(true)}
          disabled={loading || !reason.trim()}
          data-testid={`bulk-dryrun-${section.key}`}
          className="px-4 py-2 border border-canvas-line text-sm hover:border-ink disabled:opacity-50"
        >
          {loading ? "Working…" : "Dry-run (safe)"}
        </button>
        {result?.dry_run && (
          <>
            <button
              onClick={() => setConfirming(true)}
              disabled={loading || !reason.trim()}
              data-testid={`bulk-execute-${section.key}`}
              className="px-4 py-2 bg-destructive text-white text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              <Warning size={14} weight="bold" />
              Execute
            </button>
            {confirming && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-destructive font-semibold">This will write to the DB. Confirm?</span>
                <button
                  onClick={() => run(false)}
                  className="px-3 py-1 bg-destructive text-white"
                  data-testid={`bulk-confirm-${section.key}`}
                >
                  Yes, execute
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  className="px-3 py-1 border border-canvas-line"
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {result && (
        <div className="border border-canvas-line bg-canvas p-4 text-xs space-y-2" data-testid={`bulk-result-${section.key}`}>
          <div className="flex items-center gap-2">
            {result.dry_run ? (
              <>
                <Warning size={14} className="text-amber-500" />
                <span className="font-semibold">Dry-run (no writes) — {result.at}</span>
              </>
            ) : (
              <>
                <CheckCircle size={14} className="text-green-600" />
                <span className="font-semibold">Executed — {result.at}</span>
              </>
            )}
          </div>
          <div>
            <b>{section.rowNoun} affected:</b> {result[section.rowKey] ?? 0}
          </div>
          {section.countKey !== section.rowKey && (
            <div>
              <b>{section.countLabel || "Stages marked skipped"}:</b> {result[section.countKey] ?? 0}
            </div>
          )}
          {result.registrations_completed !== undefined && result.registrations_completed !== null && (
            <div>
              <b>Registrations moved to completed:</b> {result.registrations_completed}
            </div>
          )}
          {result.sales_approved !== undefined && result.sales_approved !== null && (
            <div>
              <b>Sales approved:</b> {result.sales_approved}
            </div>
          )}
          {result.cumulative_price_reduction !== undefined && (
            <div>
              <b>Cumulative price reduction across packages:</b> ₹
              {Number(result.cumulative_price_reduction).toLocaleString()}
            </div>
          )}
          {result.sales_snapshots_scrubbed !== undefined && (
            <div>
              <b>Sale snapshots cleaned:</b> {result.sales_snapshots_scrubbed}
            </div>
          )}
          {result.bookings_snapshots_scrubbed !== undefined && (
            <div>
              <b>Booking snapshots cleaned:</b> {result.bookings_snapshots_scrubbed}
            </div>
          )}
          {result.stages_to_skip !== undefined && result.stages_to_skip > 0 && (
            <div>
              <b>Pending stages to skip:</b> {result.stages_to_skip}
            </div>
          )}
          {result.registered_vehicles_scanned !== undefined && (
            <div className="font-mono text-[11px] text-ink-muted">
              &nbsp;&nbsp;source: {result.registered_vehicles_scanned} vehicles currently `registered`
            </div>
          )}
          {result.vehicles_promoted !== undefined && (
            <div>
              <b>Vehicles promoted to Registered:</b> {result.vehicles_promoted}
            </div>
          )}
          {result.reserved_before !== undefined && (
            <div className="font-mono text-[11px] text-ink-muted">
              &nbsp;&nbsp;source: {result.reserved_before} reserved + {result.sold_before} sold
            </div>
          )}
          {result.vehicles_already_registered !== undefined && (
            <div>
              <b>Vehicles already Registered:</b> {result.vehicles_already_registered}
            </div>
          )}
          {result.missing_vehicle_link !== undefined && result.missing_vehicle_link > 0 && (
            <div>
              <b className="text-amber-600">Registrations with missing vehicle link:</b>{" "}
              {result.missing_vehicle_link} (data anomaly — safe to ignore)
            </div>
          )}
          {result.cutoff_iso && (
            <div>
              <b>Cutoff:</b> {result.cutoff_iso} (rows created strictly before this date)
            </div>
          )}
          {result.by_branch && Object.keys(result.by_branch).length > 0 && (
            <div>
              <b>By branch:</b>
              <ul className="mt-1 ml-4 list-disc space-y-0.5">
                {Object.entries(result.by_branch).map(([b, n]) => (
                  <li key={b} className="font-mono">
                    {b}: {n}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminBulkClosePage() {
  return (
    <div className="space-y-6" data-testid="admin-bulk-close-page">
      <PageHeader
        eyebrow="Admin"
        title="Bulk backlog clearance"
        description="One-shot cleanup for pending Registration / Post-Sale / Sale-approval queues. Every write leaves a bulk_closed_* / bulk_approved_* audit trail so genuine work is distinguishable from clean-up. Always dry-run first."
      />
      <div className="border border-amber-500/50 bg-amber-50/40 dark:bg-amber-900/10 p-4 text-xs">
        <b>Warning</b> — irreversible on affected rows (stages will be marked skipped, sales
        marked approved). Only bulk-close rows created strictly before today. Any new
        registrations / sales added today will remain untouched so tomorrow&apos;s work
        starts from a clean slate.
      </div>
      {SECTIONS.map((s) => (
        <SectionCard key={s.key} section={s} />
      ))}
    </div>
  );
}
